import { Injectable } from '@angular/core';
import { SearchRequest, SearchResult } from 'gung-list';
import { Product, ProductService } from 'gung-standard';
import { filter, first, map, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CarLinerProductService extends ProductService {
  public getProductsByAssortment(assortmentId: string): Observable<Product[]> {
    return this.filterHiddenProducts(super.getProductsByAssortment(assortmentId));
  }

  public getProductsByAssortmentAndFlow(assortmentId: string, flow: string): Observable<Product[]> {
    return this.filterHiddenProducts(super.getProductsByAssortmentAndFlow(assortmentId, flow));
  }

  public getProductsByAssortmentExpanded(assortmentId: string): Observable<Product[]> {
    return this.filterHiddenProducts(super.getProductsByAssortmentExpanded(assortmentId));
  }

  public getProductsByAssortmentExpandedAndFlow(assortmentId: string, flowId: string): Observable<Product[]> {
    return this.filterHiddenProducts(super.getProductsByAssortmentExpandedAndFlow(assortmentId, flowId));
  }

  public getProductsByAssortmentUnfiltered(assortmentId: string): Observable<Product[]> {
    return this.filterHiddenProducts(super.getProductsByAssortmentUnfiltered(assortmentId));
  }

  public getProductsByIds(productIds: string[]): Observable<Product[]> {
    return this.filterHiddenProducts(super.getProductsByIds(productIds));
  }

  public getProductsByPriceList(priceList: string): Observable<Product[]> {
    return this.filterHiddenProducts(super.getProductsByPriceList(priceList));
  }

  public getPagedProducts(searchRequest: SearchRequest): Observable<SearchResult<Product>> {
    return super.getPagedProducts(searchRequest).pipe(
      switchMap(result =>
        this.filterHiddenProducts(of(result.items)).pipe(map(items => ({ ...result, items })))
      )
    );
  }

  /**
   * Products can be hidden for the END_USER role with the "hideForEndUser" checkbox in PIM
   */
  public isProductHiddenForCurrentUser(product: Product): Observable<boolean> {
    return this.getIsEndUser().pipe(map(isEndUser => isEndUser && this.isHiddenForEndUser(product)));
  }

  protected filterHiddenProducts(products$: Observable<Product[]>): Observable<Product[]> {
    return products$.pipe(
      switchMap(products =>
        this.getIsEndUser().pipe(
          map(isEndUser => (isEndUser ? products.filter(product => !this.isHiddenForEndUser(product)) : products))
        )
      )
    );
  }

  protected isHiddenForEndUser(product: Product): boolean {
    return String(product?.extra?.pim?.hideForEndUser) === 'true';
  }

  protected getIsEndUser(): Observable<boolean> {
    return this.authService.getRoles().pipe(
      filter(roles => !!roles),
      first(),
      map(roles => roles.includes('END_USER'))
    );
  }
}
